import { B2B_PACKAGE_MANAGEMENT, SUPPLIER_MANAGEMENT } from "api/ApiPath";
import { SupplierListRequestModel } from "api/merchantRepository/filterFeature/model/supplierList/SupplierListRequestModel";
import { BaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { ListBaseApiResponseModel } from "app/based/baseApiModel/baseApiResponseModel/ListBaseApiResponseModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { FilterInjection } from "app/filters/di/FilterInjection";
import { IFilterRepository } from "app/filters/domain/repository/IFilterRepository";
import { B2BPackageManagementInjection } from "features/packageManagement/b2bPackageManagement/di/B2BPackageManagementInjection";
import { injectable } from "tsyringe";
import { strings } from "utils/localizedStrings";
import { B2BPackagePriorityRequestEntity } from "../entities/actions/B2BPackagePriorityRequestEntity";
import { CreateB2BPackageRequestEntity } from "../entities/actions/CreateB2BPackageRequestEntity";
import { B2BPackageDetailResponseEntity } from "../entities/detail/B2BPackageDetailResponseEntity";
import { B2BInventoryExcelRequestEntity } from "../entities/inventoryForB2BPackage/B2BInventoryExcelRequestEntity";
import { InventoryForB2BPackageResponseEntity } from "../entities/inventoryForB2BPackage/InventoryForB2BPackageResponseEntity";
import { B2BPackageRequestEntity } from "../entities/mainList/B2BPackageRequestEntity";
import { B2BPackageResponseEntity } from "../entities/mainList/B2BPackageResponseEntity";
import { IB2BPackageManagementRepository } from "../repositories/IB2BPackageManagementRepository";

@injectable()
export class B2BPackageManagementUseCase {
  private packageRepo: IB2BPackageManagementRepository;
  private filterRepo: IFilterRepository;

  constructor() {
    this.packageRepo = B2BPackageManagementInjection.getRepo();
    this.filterRepo = FilterInjection.getRepo();
  }

  private buildListParams(
    params: B2BPackageRequestEntity,
  ): B2BPackageRequestEntity {
    return {
      page: params?.page ?? 0,
      limit: params?.limit ?? 10,
      group: params?.group,
      query: params?.query,
    };
  }

  private normalizeSearchInventories(
    inventories: InventoryForB2BPackageResponseEntity[] = [],
  ): InventoryForB2BPackageResponseEntity[] {
    return inventories.map((item) => ({
      ...item,
      balance: Number(item?.balance),
      maxBalance: Number(item?.maxBalance),
    }));
  }

  private mapCreatePayload(
    payload: CreateB2BPackageRequestEntity,
    inventories: InventoryForB2BPackageResponseEntity[],
  ): CreateB2BPackageRequestEntity {
    return {
      ...payload,
      description: Array.isArray(payload.description)
        ? payload.description
            .map((item) => item?.description)
            .filter(Boolean)
            .map((item) => `- ${item}.`)
            .join(" ")
        : payload.description,
      merchantInventories: inventories.map((item) => ({
        ...item,
        merchant: item?.merchant?.id ? { id: item.merchant.id } : undefined,
        id: undefined,
      })),
    };
  }

  public buildCreatePayload(
    payload: CreateB2BPackageRequestEntity,
    inventories: InventoryForB2BPackageResponseEntity[],
  ): CreateB2BPackageRequestEntity {
    return this.mapCreatePayload(payload, inventories);
  }

  public buildFromFormData(
    data: Record<string, any>,
    id?: string,
  ): InventoryForB2BPackageResponseEntity {
    return {
      id,
      code: data?.code,
      name: data?.name,
      balance: data?.balance,
      maxBalance: data?.maxBalance,
      channel: data?.channel,
      type: data?.type,
      merchant: {
        id: data?.merchant,
      },
      order: data?.order,
      sku: data?.sku,
      status: data?.status,
    };
  }

  async getDataPackageList(
    params: B2BPackageRequestEntity,
  ): Promise<ListBaseApiResponseModel<B2BPackageResponseEntity>> {
    return this.packageRepo.getDataPackageList(this.buildListParams(params));
  }

  async getPackageDetail(
    id: string,
  ): Promise<BaseApiResponseModel<B2BPackageDetailResponseEntity>> {
    return this.packageRepo.getPackageDetail(id);
  }

  async createDataPackage(
    body: CreateB2BPackageRequestEntity,
  ): Promise<BaseApiResponseModel<CreateB2BPackageRequestEntity>> {
    return this.packageRepo.createDataPackage(body);
  }

  async updateDataPackage(
    id: string,
    body: CreateB2BPackageRequestEntity,
  ): Promise<BaseApiResponseModel<CreateB2BPackageRequestEntity>> {
    return this.packageRepo.updateDataPackage(id, body);
  }

  async deleteDataPackage(
    id: string,
  ): Promise<BaseApiResponseModel<CreateB2BPackageRequestEntity>> {
    return this.packageRepo.deleteDataPackage(id);
  }

  async setTopPriority(
    body: B2BPackagePriorityRequestEntity,
  ): Promise<BaseApiResponseModel<unknown>> {
    return this.packageRepo.setTopPriority(body);
  }

  async getDataGroup(): Promise<
    ListBaseApiResponseModel<B2BPackageResponseEntity>
  > {
    return this.packageRepo.getDataGroup();
  }

  async searchInventory(
    name: string,
  ): Promise<ListBaseApiResponseModel<InventoryForB2BPackageResponseEntity>> {
    const res = await this.packageRepo.searchInventory(name);
    return {
      ...res,
      data: this.normalizeSearchInventories(res?.data),
    };
  }

  async importInventoryFromExcel(
    data: B2BInventoryExcelRequestEntity,
  ): Promise<ListBaseApiResponseModel<InventoryForB2BPackageResponseEntity>> {
    const res = await this.packageRepo.importInventoryFromExcel(data);
    return {
      ...res,
      data: this.normalizeSearchInventories(res?.data),
    };
  }

  async getSupplierList(): Promise<BaseFilterListResponseModel[]> {
    const res = await this.filterRepo.getFilterList(
      SUPPLIER_MANAGEMENT.B2B_SUPPLIER_LIST,
      new SupplierListRequestModel({ page: 0, limit: 100 }),
    );

    return [
      {
        id: strings.GlobalLabels.All,
        name: strings.GlobalLabels.All,
        code: "",
      },
      ...(res?.data || []),
    ];
  }

  async getPartnerList(): Promise<BaseFilterListResponseModel[]> {
    const res = await this.filterRepo?.getFilterList(
      B2B_PACKAGE_MANAGEMENT.B2B_PARTNER_LIST,
      {
        page: 0,
        limit: 5000,
      },
    );

    return res?.data || [];
  }
}
